import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverContentDialog, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Check, ChevronsUpDown } from "lucide-react";
import React, { Dispatch, SetStateAction, useState } from "react";

export type ComboboxProps = {
    disabled?: boolean;
    inDialog?: boolean;
    triggerLabel: string | React.ReactNode;
    onCreate?: (value: string) => void;
    open?: boolean;
    setOpen?: (open: boolean) => void;
    items: { label: string, value: string }[];
    mode?: 'single' | 'multiple';
    selectedValue: string | string[] | null;
    setSelectedValue: Dispatch<SetStateAction<string | string[] | null>> | ((value: string | string[] | null) => void) | ((value: string | null) => void);
    closeOnSelect?: boolean;
    renderSelectedValue?: (selectedValue: string | string[], items: { label: string, value: string }[]) => React.ReactNode;
}
// & {
//     mode: 'single'
//     selectedValue: string | string[] | null;
//     setSelectedValue: Dispatch<SetStateAction<string | string[] | null>> | ((value: string | string[] | null) => void) | ((value: string | null) => void);

// } & {
//     mode: 'multiple';
//     selectedValue: string | string[] | null;
//     setSelectedValue: Dispatch<SetStateAction<string | string[] | null>> | ((value: string | string[] | null) => void) | ((value: string | null) => void); 
// }

export default function Combobox({ disabled = false, triggerLabel, mode = 'single', selectedValue, setSelectedValue, open, setOpen, items = [], inDialog = false, onCreate, closeOnSelect = true, renderSelectedValue }: Readonly<ComboboxProps>) {
    const PopoverContentComponent = inDialog ? PopoverContentDialog : PopoverContent;
    const [query, setQuery] = useState<string>('');
    const [internalOpen, setInternalOpen] = useState<boolean>(false);

    const isControlled = open !== undefined && setOpen !== undefined;
    const isOpen = isControlled ? open : internalOpen;
    const handleOpenChange = (newOpen: boolean) => {
        if (isControlled) {
            setOpen(newOpen);
        } else {
            setInternalOpen(newOpen);
        }
    };

    const handleClose = () => {
        if (closeOnSelect && mode === 'single') {
            handleOpenChange(false);
        }
    };

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange} modal={!inDialog}>
            <PopoverTrigger asChild>
                <Button disabled={disabled} className="justify-between w-full px-3 font-normal text-start overflow-hidden" variant="outline">
                    {selectedValue && selectedValue.length > 0 ? (
                        <div className='flex-1 min-w-0 truncate'>
                            <span className='block truncate'>
                                {renderSelectedValue
                                    ? renderSelectedValue(selectedValue, items)
                                    : mode === 'multiple' && Array.isArray(selectedValue)
                                        ? selectedValue
                                            .map(
                                                (selectedValue: string) =>
                                                    items.find((item) => item.value === selectedValue)
                                                        ?.label
                                            )
                                            .join(', ')
                                        : items.find((item) => item.value === selectedValue)?.label}
                            </span>
                        </div>
                    ) : (
                        triggerLabel ?? 'Select Item...'
                    )}
                    <ChevronsUpDown className='w-4 h-4 ml-2 opacity-50 shrink-0' />
                </Button>
            </PopoverTrigger>
            <PopoverContentComponent className="p-1 PopoverContent">
                <Command
                    filter={(value, search) => {
                        if (value.toLocaleLowerCase().includes(search.toLocaleLowerCase())) return 1;
                        return 0;
                    }}
                >
                    <CommandInput placeholder="Search..." value={query} onValueChange={(value: string) => setQuery(value)} />
                    <CommandList >
                        <CommandEmpty
                            onClick={() => {
                                if (onCreate) {
                                    onCreate(query);
                                    setQuery('');
                                    handleClose();
                                }
                            }}
                        >
                            {onCreate ? (
                                <div className="flex gap-2 px-2">
                                    <p>Create: </p>
                                    <p className='block font-semibold truncate max-w-48 text-primary'>
                                        {query}
                                    </p>
                                </div>
                            ) : (
                                'Not found'
                            )}
                        </CommandEmpty>
                        <CommandGroup >
                            <ScrollArea className="h-[280px]" type="always">
                                {items.map((item) => (
                                    <CommandItem
                                        key={item.value}
                                        value={item.label}
                                        onSelect={(value) => {
                                            const res = items.find((item) => item.label === value) || null
                                            if (mode === 'multiple') {
                                                setSelectedValue(
                                                    selectedValue?.includes(item.value)
                                                        ? (selectedValue as string[]).filter((value) => value !== item.value)
                                                        : [...(selectedValue as string[] || []), item.value]
                                                )
                                            } else if (res?.value === "") {
                                                setSelectedValue(null)
                                            } else {
                                                setSelectedValue(
                                                    res?.value || null
                                                )
                                            }
                                            handleClose();
                                        }}
                                    >
                                        {mode === 'multiple' && (
                                            <div className={cn(
                                                "flex items-center justify-center w-4 h-4 mr-2 border border-gray-300 rounded-sm shrink-0",
                                                selectedValue?.includes(item.value) ? "bg-primary" : "bg-lighter-gray"
                                            )}>
                                                <Check className={cn(
                                                    "text-white",
                                                    selectedValue?.includes(item.value) ? "opacity-100" : "opacity-0"
                                                )} />
                                            </div>
                                        )}
                                        {item.label}
                                    </CommandItem>
                                ))}
                            </ScrollArea>
                        </CommandGroup>
                    </CommandList>
                </Command>
            </PopoverContentComponent>
        </Popover>
    )
}
